import { Typography } from "@material-ui/core";
import {
  EmployeeTypeCategoryID,
  EmployeeDto,
  EmployeePositionDto,
  PositionType,
} from "Api/Api";
import { DefaultButtonWithTooltip } from "Components/Shared/Buttons/DefaultButtonWithTooltip";
import { EmployeeFormData } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditForm";
import {
  StyledDate,
  StyledError,
  StyledNewPositionWrapper,
  StyledPositionsWrapper,
  StyledTrashIcon,
  StyledWrapper,
} from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditPositions/EmployeeEditPositionsStyles";
import { PlusIcon } from "Components/Shared/Icons/PlusIcon";
import { TrashCanIcon } from "Components/Shared/Icons/TrashCanIcon";
import { Autocomplete } from "Components/Shared/Inputs/Autocomplete";
import { DatePicker } from "Components/Shared/Inputs/DatePicker";
import * as React from "react";
import { FieldError, UseFormMethods } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getHumanResourcesPositionsAutocompleteAsync } from "State/HumanResources/HumanResourcesReducer";
import { RootStateType } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import { formatDate, clearDateTime } from "Utils/DateUtils";
import { nameof } from "Utils/ObjectUtils";
import { isEmptyArray } from "Utils/ArrayUtils";
import { cloneDeep } from "lodash";
import dayjs from "dayjs";
import { AutoAdderRegister } from "Hooks/useAutoAdder";

type Props = {
  form: UseFormMethods<EmployeeFormData>;
  employee: EmployeeDto;
  autoAdderRegister: AutoAdderRegister;
};

type NewPositionData = {
  positionID: number | null;
  positionName: string | null;
  dateFrom: string | null;
};

type NewPositionErrors = {
  position?: FieldError;
  dateFrom?: FieldError;
};

export const sortPositions = (positionsDto: EmployeePositionDto[]) => {
  return positionsDto?.sort((a, b) => {
    if (a.employeePositionID === 0) {
      return 1;
    }
    return new Date(a.dateFrom) > new Date(b.dateFrom) ? 1 : -1;
  });
};

export const EmployeeEditPositions: React.FunctionComponent<Props> = props => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const { form, employee } = props;
  const { register, watch, errors, setValue } = form;
  const { positions } = watch();
  const positionError: unknown = errors.positions;
  const { employeeTypeCategoryID } = watch();

  const getLastPosition = () => {
    return positions?.find(x => !x.dateTo);
  };

  const minNewPositionDate = () => {
    const lastPosition = getLastPosition();
    return lastPosition ? new Date(lastPosition.dateFrom) : undefined;
  };

  const [newPosition, setNewPosition] = React.useState<NewPositionData>({
    positionID: null,
    positionName: null,
    dateFrom: null,
  });

  props.autoAdderRegister("positions", () => {
    if (newPosition && (newPosition.positionID || newPosition.dateFrom)) {
      return addPosition();
    } else {
      setNewPositionErrors({});
    }

    return true;
  });

  const [
    newPositionErrors,
    setNewPositionErrors,
  ] = React.useState<NewPositionErrors>({});

  const {
    positionsAutocompleteList,
    isPositionsAutocompleteLoading,
  } = useSelector((e: RootStateType) => e.humanResources);

  React.useEffect(() => {
    register({ name: nameof<EmployeeFormData>("positions") });
  }, [register]);

  React.useEffect(() => {
    setNewPosition({
      positionID: null,
      positionName: null,
      dateFrom: null,
    });
    setValue(
      nameof<EmployeeFormData>("positions"),
      cloneDeep(employee.positions),
    );
  }, [employee, setValue]);

  const onSearch = (text: string) => {
    dispatch(
      getHumanResourcesPositionsAutocompleteAsync.request({
        search: text,
        positionType:
          employeeTypeCategoryID === EmployeeTypeCategoryID.Internal
            ? PositionType.Internal
            : PositionType.External,
      }),
    );
  };

  const clearNewPosition = () => {
    setNewPosition({
      dateFrom: null,
      positionID: null,
      positionName: null,
    });
  };

  const addPosition = () => {
    const lastPosition = getLastPosition();
    if (!validateNewPosition(lastPosition)) {
      return false;
    }

    if (lastPosition) {
      lastPosition.dateTo = setDateTo(newPosition.dateFrom);
    }

    if (positions) {
      positions.push({
        employeePositionID: 0,
        positionID: newPosition.positionID ?? 0,
        dateFrom: newPosition.dateFrom ?? "",
        dateTo: "",
        positionName: newPosition.positionName ?? "",
      });
    }
    clearNewPosition();

    setValue(nameof<EmployeeFormData>("positions"), positions, {
      shouldValidate: true,
    });

    return true;
  };

  const validateNewPosition = (lastPosition = getLastPosition()) => {
    const minDate = minNewPositionDate();
    const isDateValid =
      newPosition.dateFrom &&
      (!minDate || clearDateTime(new Date(newPosition.dateFrom)) >= minDate);

    const dateFromValidation = isDateValid
      ? undefined
      : {
          type: "required",
          message: t(
            Resources.HumanResources.EmployeeEdit.Positions.DateFromValidation,
          ),
        };

    const positionValidation =
      newPosition.positionID &&
      lastPosition?.positionID !== newPosition.positionID
        ? undefined
        : {
            type: "required",
            message: t(
              lastPosition?.positionID === newPosition.positionID
                ? Resources.HumanResources.EmployeeEdit.Positions
                    .PositionValidation.EqualsLastPosition
                : Resources.HumanResources.EmployeeEdit.Positions
                    .PositionValidation.Empty,
            ),
          };

    setNewPositionErrors({
      position: positionValidation,
      dateFrom: dateFromValidation,
    });

    return !dateFromValidation && !positionValidation;
  };

  const removeNewPosition = (position?: EmployeePositionDto) => {
    clearNewPosition();

    const pos = positions?.filter(x => x !== position) ?? [];
    const lastPosition = sortPositions(pos)[pos?.length - 1];
    if (lastPosition) {
      lastPosition.dateTo = "";
    }

    setValue(nameof<EmployeeFormData>("positions"), pos);
  };

  const setDateTo = (date: Date | string | undefined | null) => {
    if (!date) {
      return "";
    }

    let dateTo = dayjs(date);
    dateTo = dateTo.subtract(1, "day");

    return dateTo.toISOString();
  };

  return (
    <div>
      <div className={"form-title"}>
        {t(Resources.HumanResources.EmployeeEdit.Positions.Label)}
      </div>

      <StyledWrapper>
        {!isEmptyArray(positions) && (
          <StyledPositionsWrapper>
            {sortPositions(positions ?? [])?.map((position, index) => (
              <React.Fragment
                key={`${position.employeePositionID}-${position.positionID}`}
              >
                <div>
                  <Typography>{position.positionName}</Typography>
                  <StyledDate>
                    {`${t(
                      Resources.HumanResources.EmployeeEdit.Positions.DateFrom,
                    )}: ${formatDate(position.dateFrom)} ${t(
                      Resources.HumanResources.EmployeeEdit.Positions.DateTo,
                    )} ${formatDate(position.dateTo)}`}
                    {index === (positions?.length ?? 1) - 1 && (
                      <StyledTrashIcon>
                        <DefaultButtonWithTooltip
                          title={t(
                            Resources.HumanResources.EmployeeEdit.Positions
                              .Remove,
                          )}
                          onClick={() => removeNewPosition(position)}
                        >
                          <TrashCanIcon />
                        </DefaultButtonWithTooltip>
                      </StyledTrashIcon>
                    )}
                  </StyledDate>
                </div>
              </React.Fragment>
            ))}
          </StyledPositionsWrapper>
        )}

        <StyledError>
          {positionError && (
            <Typography>
              {(positionError as FieldError | undefined)?.message}
            </Typography>
          )}
        </StyledError>

        <StyledNewPositionWrapper>
          <Autocomplete
            value={
              newPosition.positionID
                ? {
                    itemID: newPosition.positionID,
                    name: newPosition.positionName ?? "",
                  }
                : null
            }
            onSearch={onSearch}
            isLoading={isPositionsAutocompleteLoading}
            options={positionsAutocompleteList ?? []}
            onChange={val => {
              setNewPositionErrors({
                ...newPositionErrors,
                position: undefined,
              });
              setNewPosition({
                ...newPosition,
                positionID: val?.itemID ?? null,
                positionName: val?.name ?? null,
              });
            }}
            label={t(Resources.HumanResources.EmployeeEdit.Positions.Label)}
            fieldError={newPositionErrors.position}
          />

          <DatePicker
            value={newPosition.dateFrom}
            label={t(Resources.HumanResources.EmployeeEdit.Positions.DateFrom)}
            onChange={val => {
              setNewPositionErrors({
                ...newPositionErrors,
                dateFrom: undefined,
              });
              setNewPosition({
                ...newPosition,
                dateFrom: val,
              });
            }}
            fieldError={newPositionErrors.dateFrom}
            minDate={minNewPositionDate()}
          />

          <DefaultButtonWithTooltip
            title={t(Resources.HumanResources.EmployeeEdit.Positions.Add)}
            onClick={addPosition}
          >
            <PlusIcon />
          </DefaultButtonWithTooltip>

          <StyledError>
            {newPositionErrors.position && (
              <Typography>{newPositionErrors.position?.message}</Typography>
            )}
          </StyledError>
          <StyledError>
            {newPositionErrors.dateFrom && (
              <Typography>{newPositionErrors.dateFrom?.message}</Typography>
            )}
          </StyledError>
        </StyledNewPositionWrapper>
      </StyledWrapper>
    </div>
  );
};
